import { ComponentProps, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanySearchRow } from '../../services/service-company-search';
import CompanySearch from '../app-company-search/CompanySearch';
import {
  CompanySearchLink,
  CompanySearchUnavailable,
} from '../app-company-search/CompanySearchView';
import useCompanySearchAvailability from '../app-company-search/useCompanySearchAvailability';
import OrgNumberField from './OrgNumberField';

type Props = {
  orgNumber: Omit<ComponentProps<typeof OrgNumberField>, 'inputRef' | 'note'>;
  picked: CompanySearchRow | null;
  manualEntry: boolean;
  onCompanyChange: (company: CompanySearchRow | null) => void;
  onManualEntryChange: (manualEntry: boolean) => void;
};

export default function OrganizationCompany({
  orgNumber,
  picked,
  manualEntry,
  onCompanyChange,
  onManualEntryChange,
}: Props) {
  const [t] = useTranslation('organizations', { keyPrefix: 'add' });
  const [tCompanySearch] = useTranslation('companySearch');
  const { down, onUnavailable, typedFieldRef } = useCompanySearchAvailability();
  const searchRef = useRef<HTMLInputElement>(null);
  const focusAfterEntrySwitch = useRef(false);

  useEffect(() => {
    if (!focusAfterEntrySwitch.current) return;
    focusAfterEntrySwitch.current = false;
    (manualEntry ? typedFieldRef : searchRef).current?.focus();
  }, [manualEntry]);

  const enterOrgNumberManually = () => {
    focusAfterEntrySwitch.current = true;
    onManualEntryChange(true);
  };

  const returnToCompanySearch = () => {
    focusAfterEntrySwitch.current = true;
    onManualEntryChange(false);
    orgNumber.onClear();
  };

  return (
    <>
      {down === 'unavailable' && <CompanySearchUnavailable />}
      {manualEntry ? (
        <OrgNumberField
          {...orgNumber}
          inputRef={typedFieldRef}
          note={
            !down && (
              <CompanySearchLink onClick={returnToCompanySearch}>
                {t('button.searchRegister', 'Sök i företagsregistret i stället')}
              </CompanySearchLink>
            )
          }
        />
      ) : down ? (
        <OrgNumberField
          {...orgNumber}
          inputRef={typedFieldRef}
          note={
            <CompanySearchLink onClick={enterOrgNumberManually}>
              {tCompanySearch('manualEntry.enter', 'Ange organisationsnummer manuellt')}
            </CompanySearchLink>
          }
        />
      ) : (
        <CompanySearch
          value={picked}
          onChange={onCompanyChange}
          onUnavailable={onUnavailable}
          label={t('label.company', 'Företag')}
          required
          errorMessage={orgNumber.errorMessage || orgNumber.requiredMessage}
          onEnterManually={enterOrgNumberManually}
          inputRef={searchRef}
        />
      )}
    </>
  );
}
